/**
 * Dados da seção "Clínica e responsáveis" da CAT (#313): tipos, a busca das opções e os
 * nomes a mostrar. Fica fora do componente para o arquivo dele exportar só componente.
 */

import { useCallback } from 'react';
import { useDadoPermitido } from '../../../authorization/useListaPermitida';
import { catService } from '../../../services/catService';
import type { Cat, CatFormData, CatProviderOption, CatProviderOptions } from '../../../types/cat';

type CampoDePrestador =
  | 'clinicId'
  | 'medicalResponsibleId'
  | 'technicalResponsibleId'
  | 'environmentalResponsibleId';

export type ValoresDePrestadores = Pick<CatFormData, CampoDePrestador>;

export type EstadoDasOpcoes = 'sem-empresa' | 'carregando' | 'pronto' | 'indisponivel';

export interface OpcoesDePrestadores {
  estado: EstadoDasOpcoes;
  opcoes: CatProviderOptions;
}

export type PrestadoresGravados = Pick<
  Cat,
  'clinic' | 'medicalResponsible' | 'technicalResponsible' | 'environmentalResponsible'
>;

const SEM_OPCOES: CatProviderOptions = {
  clinicas: [],
  responsaveisMedicos: [],
  responsaveisTecnicos: [],
  responsaveisAmbientais: [],
};

type OpcoesDaEmpresa = CatProviderOptions & { companyId: number };

/**
 * Busca as opções da empresa escolhida. A resposta carrega o id da empresa a que pertence:
 * trocando de empresa, a lista da anterior não passa por atual enquanto a nova não chega.
 * `cat.create` e `cat.update` são as permissões que o servidor aceita nesta rota; a tela de
 * cadastro cobra a primeira e a de edição, a segunda.
 */
export function useOpcoesDePrestadores(
  companyId: number | undefined,
  permissao: 'cat.create' | 'cat.update',
): OpcoesDePrestadores {
  const carregar = useCallback(async (): Promise<OpcoesDaEmpresa | null> => {
    if (companyId === undefined) return null;
    const opcoes = await catService.getProviderOptions(companyId);
    return { companyId, ...opcoes };
  }, [companyId]);

  const busca = useDadoPermitido<OpcoesDaEmpresa | null>(
    permissao,
    'Clínica e responsáveis',
    carregar,
    { habilitado: companyId !== undefined },
  );

  if (companyId === undefined) return { estado: 'sem-empresa', opcoes: SEM_OPCOES };
  if (!busca.resolvida) return { estado: 'carregando', opcoes: SEM_OPCOES };
  if (!busca.permitido || busca.erro) return { estado: 'indisponivel', opcoes: SEM_OPCOES };
  if (busca.dado && busca.dado.companyId === companyId) return { estado: 'pronto', opcoes: busca.dado };
  return { estado: 'carregando', opcoes: SEM_OPCOES };
}

export const ROTULO_CLINICA_FORA_DA_LISTA = 'Clínica já vinculada (fora da lista atual)';
export const ROTULO_RESPONSAVEL_FORA_DA_LISTA = 'Responsável já vinculado (fora da lista atual)';

/**
 * O nome a mostrar para um id: o da lista e, se o vínculo já saiu dela, o da CAT gravada. Sem
 * nome em lugar nenhum, o rótulo neutro — o id continua escolhido e a tela precisa dizer isso.
 */
export const nomeDoPrestador = (
  id: number | '' | undefined,
  lista: CatProviderOption[],
  gravado?: CatProviderOption,
  rotuloNeutro?: string,
): string | undefined => {
  if (!id) return undefined;
  const daLista = lista.find((opcao) => opcao.id === Number(id));
  if (daLista) return daLista.nome;
  return gravado?.id === Number(id) ? gravado.nome : rotuloNeutro;
};

export const gravadoDaClinica = (cat?: PrestadoresGravados): CatProviderOption | undefined =>
  cat?.clinic ? { id: Number(cat.clinic.id), nome: cat.clinic.tradeName || cat.clinic.legalName } : undefined;

export const gravadoDoResponsavel = (
  responsavel?: { id: number; name: string } | null,
): CatProviderOption | undefined =>
  responsavel ? { id: Number(responsavel.id), nome: responsavel.name } : undefined;
